import { Box, Typography } from "@mui/material";
import type { DateTime } from "luxon";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { MetaChip } from "../../components/UIToolkit/Badges/MetaChip";
import { LabeledField } from "../../components/UIToolkit/molecules/LabeledField";
import { ReadOnlyMetadataField } from "../../components/UIToolkit/molecules/ReadOnlyMetadataField";
import { FONT_SIZE, INK, NEUTRAL } from "../../theme/tokens";
import { humanizeSlug, reasonLabel } from "./shiftLineage";
import { SECTION_LABEL_SX, STEP_LABEL_KEY, STEP_TONE } from "./shiftLineageDisplay";
import { type ShiftLineageGroup, type ShiftRunStep, toDateTime } from "./shiftLineageGroups";

/**
 * The sync timeline of ONE shift: one block per sync run that touched it, in the order it happened —
 * what the run did, when, why, the raw punches behind it and the source file it read.
 *
 * Shared by both readings of the same data: the grid row's expanded detail area (clamped to the pixel
 * height the grid reserved) and the single-shift focus panel a support user lands on from a shift's raw
 * punches. Prop-driven — no data fetching, no page state — so a third surface can render it as is.
 */

/**
 * Detail-row geometry (px). The grid needs an explicit pixel height for an expanded row — auto-height
 * attaches a per-row ResizeObserver that thrashes virtualization (see `SystemDataGrid`) — so the height
 * is ESTIMATED from the content, clamped, and the inner area scrolls past the clamp. It lives beside the
 * markup it measures: change a row here and the estimate must change with it.
 */
export const EXPAND_ROW = {
	/** `expandedContentSx={{ py: 1.5 }}` — 12px top + 12px bottom. */
	PADDING: 24,
	/** Per sync step: its header row. */
	STEP_HEADER: 76,
	/** One punch renders as a single row of labelled fields at desktop widths. */
	PUNCH_ROW: 62,
	/** One row of the 4-up `detail` grid. */
	DETAIL_ROW: 58,
	/** Slack so a short row lands just above its content instead of scrolling for the last few pixels. */
	SAFETY: 12,
	/** The source-file field, when the run archived one. */
	FILE_FIELD: 60,
	/** The "Sync timeline" heading plus the gaps between steps. */
	HEADING: 48,
	MIN: 180,
	/** Beyond this the detail area scrolls internally rather than pushing the table around. */
	MAX: 560,
} as const;

/** Estimated full height of a row's expanded detail area, clamped to the scrollable budget. */
export const expandHeight = (row: ShiftLineageGroup): number => {
	const content = row.steps.reduce((total, step) => {
		const punches = step.changes.flatMap((change) => change.punchIdentity ?? []).length;
		const detailRows = Math.ceil(Object.keys(step.changes[step.changes.length - 1]?.detail ?? {}).length / 4);
		return total + EXPAND_ROW.STEP_HEADER + punches * EXPAND_ROW.PUNCH_ROW + detailRows * EXPAND_ROW.DETAIL_ROW + (step.fileS3Key ? EXPAND_ROW.FILE_FIELD : 0);
	}, 0);
	return Math.min(Math.max(content + EXPAND_ROW.HEADING + EXPAND_ROW.SAFETY, EXPAND_ROW.MIN), EXPAND_ROW.MAX);
};

/** Render any heterogeneous `detail` value as a readable string (dates formatted in the dept timezone). */
const stringifyDetailValue = (value: unknown, timezone: string): string => {
	if (value === null || value === undefined) return "—";
	const dt = toDateTime(value);
	if (dt) return dt.setZone(timezone).toFormat("MMM d, yyyy HH:mm");
	if (typeof value === "boolean") return String(value);
	if (typeof value === "object") return JSON.stringify(value);
	return String(value);
};

type Props = {
	steps: ShiftRunStep[];
	/** Department timezone — every instant is rendered in it, never in the viewer's zone. */
	timezone: string;
	/** Stable prefix for the per-step test ids (the shift this timeline is about). */
	subjectKey: string;
	/** Clamp + internal scroll, for the grid's fixed-height detail row. Omit to grow with the content. */
	maxHeight?: number;
};

export const ShiftSyncTimeline: FC<Props> = ({ steps, timezone, subjectKey, maxHeight }) => {
	const { t } = useTranslation();

	const formatWhen = (value: DateTime | null): string => value?.setZone(timezone).toFormat("MMM d, HH:mm") ?? "—";

	/** One sync run's entry in the timeline: what it did, when, and the evidence behind it. */
	const renderStep = (step: ShiftRunStep, index: number) => {
		const punches = step.changes.flatMap((change) => change.punchIdentity ?? []);
		const detailEntries = Object.entries(step.changes[step.changes.length - 1]?.detail ?? {});
		return (
			<Box
				key={step.syncRunId}
				sx={{
					display: "flex",
					flexDirection: "column",
					gap: 1.25,
					p: 1.5,
					borderRadius: 1.5,
					bgcolor: NEUTRAL[50],
					// A left rule ties the run's rows together as one step in the sequence.
					borderLeft: `3px solid ${NEUTRAL[200]}`,
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
					<Typography sx={{ ...SECTION_LABEL_SX }}>{t("employeeChangelog.shiftLineage.expand.syncStep", { index: index + 1 })}</Typography>
					<MetaChip label={t(STEP_LABEL_KEY[step.status])} tone={STEP_TONE[step.status]} dataTestId={`shift-lineage-timeline-step-${subjectKey}-${index}`} />
					<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.SECONDARY }}>{formatWhen(step.at)}</Typography>
					<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.MUTED }}>{step.reasons.map((reason) => reasonLabel(t, reason)).join(" · ")}</Typography>
				</Box>

				{punches.length > 0 && (
					<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
						<Typography sx={{ ...SECTION_LABEL_SX }}>{t("employeeChangelog.shiftLineage.expand.punches")}</Typography>
						{punches.map((punch, punchIndex) => (
							<Box
								key={punch.actual_punch_id ?? `${punch.external_employee_id}-${punch.event_time}-${punchIndex}`}
								sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(3, 1fr)", lg: "repeat(6, 1fr)" }, gap: 1.5 }}
							>
								<LabeledField
									label={t("employeeChangelog.shiftLineage.punch.time")}
									value={toDateTime(punch.event_time)?.setZone(timezone).toFormat("MMM d, yyyy HH:mm") ?? "—"}
								/>
								<LabeledField label={t("employeeChangelog.shiftLineage.punch.type")} value={punch.event_type ?? "—"} />
								<LabeledField label={t("employeeChangelog.shiftLineage.punch.employeeId")} value={punch.external_employee_id ?? "—"} />
								<LabeledField label={t("employeeChangelog.shiftLineage.punch.hrisDepartment")} value={punch.hris_department_id ?? "—"} />
								<LabeledField
									label={t("employeeChangelog.shiftLineage.punch.earningCodes")}
									value={punch.raw_earning_codes?.length ? punch.raw_earning_codes.join(", ") : "—"}
								/>
								<LabeledField label={t("employeeChangelog.shiftLineage.punch.pairToken")} value={punch.pair_token ?? "—"} />
							</Box>
						))}
					</Box>
				)}

				{detailEntries.length > 0 && (
					<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
						<Typography sx={{ ...SECTION_LABEL_SX }}>{t("employeeChangelog.shiftLineage.expand.details")}</Typography>
						<Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" }, gap: 1.5 }}>
							{detailEntries.map(([key, value]) => (
								<LabeledField key={key} label={humanizeSlug(key)} value={stringifyDetailValue(value, timezone)} />
							))}
						</Box>
					</Box>
				)}

				{step.fileS3Key && (
					<ReadOnlyMetadataField
						label={t("employeeChangelog.shiftLineage.file.label")}
						value={step.fileS3Key}
						copyable
						dataTestId={`shift-lineage-expand-file-${subjectKey}-${index}`}
					/>
				)}
			</Box>
		);
	};

	return (
		<Box
			data-testid={`shift-sync-timeline-${subjectKey}`}
			sx={{
				display: "flex",
				flexDirection: "column",
				gap: 1.5,
				// Bounded in px, not `height: 100%`: the detail cell grows with its content, so a percentage
				// height resolves against the grown parent, `overflow` never engages, and a many-sync shift
				// spills outside the row the grid reserved for it.
				...(maxHeight === undefined ? {} : { maxHeight, overflow: "auto" }),
			}}
		>
			<Typography sx={{ ...SECTION_LABEL_SX }}>{t("employeeChangelog.shiftLineage.expand.timeline")}</Typography>
			{steps.map((step, index) => renderStep(step, index))}
		</Box>
	);
};
